type ValidationError = {
  field: string;
  message: string;
};

const GENERIC_WAF_BLOCK_MESSAGE =
  'Your request was blocked by our security protection. Please try again or contact support if this persists.';

/**
 * @registry Reads the machine-readable `code` the API attaches to some error bodies (e.g. REAUTHENTICATION_REQUIRED).
 */
export function parseErrorCode(error: any): string | undefined {
  const rawBody =
    typeof error?.response === 'string'
      ? error.response
      : error?.response?.data ?? error?.response;
  try {
    const parsed = typeof rawBody === 'string' ? JSON.parse(rawBody) : rawBody;
    return typeof parsed?.code === 'string' ? parsed.code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @registry Extracts a human-readable message from an API error, handling WAF blocks and various error shapes.
 */
export function parseErrorMessage(error: any): string {
  console.log('Experienced error: ', JSON.stringify(error));
  // Thunk rejections (rejectWithValue) are already-parsed messages.
  if (typeof error === 'string' && error.length > 0) return error;

  const status: number | undefined = error?.status ?? error?.response?.status;
  const rawBody =
    typeof error?.response === 'string'
      ? error.response
      : error?.response?.data ?? error?.response;

  try {
    const parsed = typeof rawBody === 'string' ? JSON.parse(rawBody) : rawBody;
    if (parsed?.error === 'WAF_BLOCK' && typeof parsed?.message === 'string') {
      return parsed.message;
    }
  } catch {
    // Not JSON or not a WAF body — fall through to the legacy logic below.
  }

  if (status === 403) {
    const bodyEmpty =
      !rawBody || (typeof rawBody === 'string' && rawBody.trim().length === 0);
    if (bodyEmpty) {
      return GENERIC_WAF_BLOCK_MESSAGE;
    }
  }

  try {
    if (error instanceof Object) {
      if (error?.response instanceof String) {
        return error.response;
      }

      if (
        error?.response?.data instanceof Object &&
        !!error?.response?.data?.message
      ) {
        return error?.response?.data?.message;
      }

      try {
        const obj = JSON.parse(error?.response);

        // If we have an error array, we show that.
        if (obj.errors) {
          const errors = (obj.errors as ValidationError[]).map(
            error => error.message
          );
          return errors.join(', ');
        }

        return obj.message;
      } catch {
        // Ignore
      }

      if (error?.response?.message) {
        return error.response.message;
      }

      if (error?.message) {
        return error.message;
      }
    }

    if (error?.message) {
      return error.message;
    }

    const obj = JSON.parse(error.response);
    return obj.message || error.message;
  } catch (err: any) {
    console.error('Origin: %s, New: %s', error, err);
    return 'Unexpected Server Error was encountered.';
  }
}
